import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  UserPlus,
  Link2,
  Share2,
  Home,
  Mail,
  RefreshCw,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JointTenancy() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('create');
  const [loading, setLoading] = useState(false);
  
  // Create joint application state
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [maxTenants, setMaxTenants] = useState(2);
  const [createdShareCode, setCreatedShareCode] = useState(null);
  
  // Join state
  const [shareCode, setShareCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  
  // My joint applications state
  const [myJointApps, setMyJointApps] = useState([]);
  
  // Fetch user's applications
  const fetchApplications = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/applications/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to non-joint applications in draft/submitted status
        const eligible = (data.applications || data || []).filter(
          app => !app.is_joint_application && ['draft', 'submitted'].includes(app.status)
        );
        setApplications(eligible);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, [token]);
  
  // Fetch my joint applications
  const fetchMyJointApps = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/applications/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const apps = data.applications || data || [];
        
        // Get joint status for each
        const jointApps = [];
        for (const app of apps) {
          if (app.is_joint_application) {
            try {
              const statusRes = await fetch(`${API_URL}/api/tenancy-lifecycle/joint/status/${app.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (statusRes.ok) {
                const status = await statusRes.json();
                jointApps.push({ ...app, jointStatus: status });
              }
            } catch (e) {
              jointApps.push({ ...app, jointStatus: null });
            }
          }
        }
        setMyJointApps(jointApps);
      }
    } catch (error) {
      console.error('Failed to fetch joint apps:', error);
    }
  }, [token]);
  
  useEffect(() => {
    fetchApplications();
    fetchMyJointApps();
  }, [fetchApplications, fetchMyJointApps]);
  
  // Handle URL params for share code
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setShareCode(code);
      setActiveTab('join');
    }
  }, [searchParams]);
  
  // Create joint application
  const handleCreateJoint = async () => {
    if (!selectedApplication) {
      toast.error('Please select an application');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/joint/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          application_id: selectedApplication,
          max_tenants: maxTenants
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setCreatedShareCode(data.share_code);
        toast.success('Joint tenancy created! Share the code with your co-tenant.');
        fetchMyJointApps();
      } else {
        toast.error(data.detail || 'Failed to create joint tenancy');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  // Join with share code
  const handleJoinWithCode = async () => {
    if (!shareCode.trim()) {
      toast.error('Please enter a share code');
      return;
    }
    
    setJoinLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/joint/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          share_code: shareCode.trim().toUpperCase()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Successfully joined the application!');
        setShareCode('');
        fetchMyJointApps();
        setActiveTab('my-apps');
      } else {
        toast.error(data.detail?.error || data.detail || 'Invalid share code');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setJoinLoading(false);
    }
  };
  
  // Copy share code
  const copyShareCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Share code copied to clipboard!');
  };
  
  // Leave joint application
  const handleLeaveApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to leave this joint application?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/joint/leave/${applicationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('You have left the joint application');
        fetchMyJointApps();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to leave application');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="joint-tenancy-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Joint Tenancy</h1>
            <p className="text-gray-600">Apply together with your housemates</p>
          </div>
          <Button variant="outline" onClick={() => { fetchApplications(); fetchMyJointApps(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="create" data-testid="create-tab">
              <UserPlus className="w-4 h-4 mr-2" />
              Create Joint App
            </TabsTrigger>
            <TabsTrigger value="join" data-testid="join-tab">
              <Link2 className="w-4 h-4 mr-2" />
              Join with Code
            </TabsTrigger>
            <TabsTrigger value="my-apps" data-testid="my-apps-tab">
              <Users className="w-4 h-4 mr-2" />
              My Joint Apps
            </TabsTrigger>
          </TabsList>
          
          {/* Create Tab */}
          <TabsContent value="create" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Create a Joint Application</CardTitle>
                <CardDescription>
                  Convert your existing application to a joint tenancy. You'll receive a share code to give to your co-tenants.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {createdShareCode ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Share Code Created!</h3>
                      <p className="text-green-700 mb-4">Share this code with your co-tenant:</p>
                      <div className="flex items-center justify-center gap-3">
                        <div className="text-3xl font-mono font-bold bg-white px-6 py-3 rounded-lg border-2 border-green-300">
                          {createdShareCode}
                        </div>
                        <Button onClick={() => copyShareCode(createdShareCode)} variant="outline">
                          <Copy className="w-5 h-5" />
                        </Button>
                      </div>
                      <p className="text-sm text-green-600 mt-4">
                        This code expires in 14 days. Your co-tenant can use it on the "Join with Code" tab.
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => setCreatedShareCode(null)} className="w-full">
                      Create Another
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Select Your Application</Label>
                      <select
                        className="w-full border rounded-lg p-3"
                        value={selectedApplication}
                        onChange={(e) => setSelectedApplication(e.target.value)}
                        data-testid="application-select"
                      >
                        <option value="">-- Select an application --</option>
                        {applications.map((app) => (
                          <option key={app.id} value={app.id}>
                            {app.property?.address_line_1 || app.property_address || 'Application'} - {app.status}
                          </option>
                        ))}
                      </select>
                      {applications.length === 0 && (
                        <p className="text-sm text-amber-600">
                          No eligible applications found. You need an application in draft or submitted status.
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Total Number of Tenants (including you)</Label>
                      <select
                        className="w-full border rounded-lg p-3"
                        value={maxTenants}
                        onChange={(e) => setMaxTenants(parseInt(e.target.value))}
                        data-testid="max-tenants-select"
                      >
                        <option value={2}>2 tenants</option>
                        <option value={3}>3 tenants</option>
                        <option value={4}>4 tenants</option>
                        <option value={5}>5 tenants</option>
                      </select>
                    </div>
                  </>
                )}
              </CardContent>
              {!createdShareCode && (
                <CardFooter>
                  <Button 
                    onClick={handleCreateJoint} 
                    disabled={loading || !selectedApplication}
                    className="w-full"
                    data-testid="create-joint-btn"
                  >
                    {loading ? 'Creating...' : 'Create Joint Application'}
                  </Button>
                </CardFooter>
              )}
            </Card>
          </TabsContent>
          
          {/* Join Tab */}
          <TabsContent value="join" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Join a Joint Application</CardTitle>
                <CardDescription>
                  Enter the share code from your lead tenant to join their application.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Share Code</Label>
                  <Input
                    placeholder="XXXX-XXXX"
                    value={shareCode}
                    onChange={(e) => setShareCode(e.target.value.toUpperCase())}
                    className="text-center text-2xl font-mono tracking-widest"
                    maxLength={9}
                    data-testid="share-code-input"
                  />
                  <p className="text-sm text-gray-500">
                    Format: XXXX-XXXX (letters and numbers)
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">What happens when you join?</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• You'll be added to the joint application</li>
                    <li>• You'll need to complete your own verification</li>
                    <li>• All tenants must be verified before the application proceeds</li>
                    <li>• You can leave the application at any time</li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleJoinWithCode}
                  disabled={joinLoading || !shareCode.trim()}
                  className="w-full"
                  data-testid="join-btn"
                >
                  {joinLoading ? 'Joining...' : 'Join Application'}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          {/* My Joint Apps Tab */}
          <TabsContent value="my-apps" className="mt-6">
            <div className="space-y-4">
              {myJointApps.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-medium text-gray-600">No Joint Applications</h3>
                    <p className="text-sm text-gray-500 mt-2">
                      Create a new joint application or join one with a share code.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                myJointApps.map((app) => (
                  <Card key={app.id} data-testid={`joint-app-${app.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Home className="w-5 h-5" />
                            {app.property?.address_line_1 || app.property_address || 'Property'}
                          </CardTitle>
                          <CardDescription>
                            {app.property?.city} {app.property?.postcode}
                          </CardDescription>
                        </div>
                        <Badge variant={app.status === 'submitted' ? 'default' : 'secondary'}>
                          {app.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {app.jointStatus && (
                        <div className="space-y-4">
                          {/* Share Code */}
                          {app.jointStatus.share_code && (
                            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                              <span className="text-sm text-gray-600">Share Code:</span>
                              <span className="font-mono font-bold">{app.jointStatus.share_code}</span>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => copyShareCode(app.jointStatus.share_code)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Progress */}
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all"
                                style={{ width: `${(app.jointStatus.joined_count / app.jointStatus.max_tenants) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">
                              {app.jointStatus.joined_count}/{app.jointStatus.max_tenants} joined
                            </span>
                          </div>
                          
                          {/* Members */}
                          <div className="space-y-2">
                            <h4 className="font-medium text-sm">Tenants</h4>
                            {app.jointStatus.members?.map((member, idx) => (
                              <div 
                                key={member.tenant_id} 
                                className="flex items-center justify-between p-2 bg-gray-50 rounded"
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    member.is_lead ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {member.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{member.name}</p>
                                    <p className="text-xs text-gray-500">{member.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {member.is_lead && (
                                    <Badge variant="outline" className="text-xs">Lead</Badge>
                                  )}
                                  {member.verified ? (
                                    <Badge className="bg-green-100 text-green-700">Verified</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      Pending
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* All Joined Status */}
                          {app.jointStatus.all_joined && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <span className="text-green-700">All tenants have joined!</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="border-t pt-4">
                      <div className="flex gap-2 w-full">
                        <Button 
                          variant="outline" 
                          onClick={() => navigate(`/applications/${app.id}`)}
                          className="flex-1"
                        >
                          View Application
                        </Button>
                        {app.jointStatus?.members?.find(m => m.tenant_id === user?.id && !m.is_lead) && (
                          <Button 
                            variant="destructive"
                            onClick={() => handleLeaveApplication(app.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Leave
                          </Button>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
